import { useEffect, useId, useState } from 'react'
import { api, ErrorDeApi, type ConsentimientoDeSoporte } from '../servicios/api'
import { useTrampaDeFoco } from '../ganchos/useTrampaDeFoco'
import { telon } from '../ganchos/telon'

const DIAS_MAXIMOS = 30

/**
 * Consentimiento del despacho para que soporte de la plataforma vea su contenido contable
 * (RF-107, §6.4). Solo el Administrador lo otorga o revoca; cada acceso bajo consentimiento
 * queda en la bitácora del despacho atribuido al personal de plataforma.
 */
export function ConsentimientosDeSoporte() {
  const [consentimientos, setConsentimientos] = useState<ConsentimientoDeSoporte[]>([])
  const [error, setError] = useState('')
  const [mostrarOtorgar, setMostrarOtorgar] = useState(false)
  // E12 · El último `window.confirm` que quedaba en la SPA. Los otros seis ya se habían
  // reemplazado; este se quedó atrás porque vive dentro de Usuarios y no en su propia página.
  const [revocando, setRevocando] = useState<ConsentimientoDeSoporte | null>(null)

  async function recargar() {
    try {
      setConsentimientos(await api.listarConsentimientosDeSoporte())
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudieron cargar los consentimientos de soporte.')
    }
  }

  useEffect(() => {
    void recargar()
  }, [])

  async function revocar(consentimiento: ConsentimientoDeSoporte) {
    setRevocando(null)
    setError('')
    try {
      await api.revocarConsentimientoDeSoporte(consentimiento.id)
      await recargar()
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo revocar el consentimiento.')
    }
  }

  const activo = consentimientos.find((c) => c.activo)

  return (
    <section style={{ marginTop: 'var(--space-4)' }} aria-labelledby="titulo-soporte">
      <header className="encabezado">
        <h2 id="titulo-soporte">Soporte de la plataforma</h2>
        <button className="primario" onClick={() => setMostrarOtorgar(true)} disabled={Boolean(activo)}>
          Otorgar consentimiento
        </button>
      </header>
      <p className="nota">
        El personal de ContaQuick no puede ver la contabilidad de tu despacho sin un consentimiento con propósito y
        vigencia definidos, otorgado por un Administrador. Cada acceso queda en tu bitácora y puedes revocarlo cuando
        quieras (RF-107).
      </p>

      {error && <p className="error" role="alert">{error}</p>}

      <div className="tabla-envoltura">
      <table className="tabla">
        <thead>
          <tr>
            <th>Propósito</th>
            <th>Otorgado</th>
            <th>Vigente hasta</th>
            <th>Estado</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {consentimientos.map((c) => (
            <tr key={c.id}>
              <td>{c.proposito}</td>
              <td className="sutil">{new Date(c.creadoEnUtc).toLocaleString('es-MX')}</td>
              <td className="sutil">{new Date(c.vigenteHastaUtc).toLocaleString('es-MX')}</td>
              <td>
                <span className={`insignia ${c.activo ? 'vigente' : 'vencido'}`}>
                  {c.activo ? 'Activo' : c.revocadoEnUtc ? 'Revocado' : 'Vencido'}
                </span>
              </td>
              <td>
                {c.activo && (
                  <button className="enlace" onClick={() => setRevocando(c)}>Revocar</button>
                )}
              </td>
            </tr>
          ))}
          {consentimientos.length === 0 && (
            <tr>
              <td colSpan={5} className="vacio">Nunca se ha otorgado un consentimiento de soporte: nadie de la plataforma ha podido ver tu contabilidad.</td>
            </tr>
          )}
        </tbody>
      </table>
      </div>

      {mostrarOtorgar && (
        <FormularioDeConsentimiento
          onCerrar={() => setMostrarOtorgar(false)}
          onOtorgado={() => {
            setMostrarOtorgar(false)
            void recargar()
          }}
        />
      )}

      {revocando && (
        <ModalDeRevocacionDeSoporte
          consentimiento={revocando}
          onCerrar={() => setRevocando(null)}
          onConfirmar={() => void revocar(revocando)}
        />
      )}
    </section>
  )
}

/**
 * E12 · Confirmación de revocación del consentimiento de soporte (RF-107, §6.4).
 *
 * El `window.confirm` que había decía «Soporte dejará de ver la información del despacho de
 * inmediato» y ahí se acababa. Lo que un administrador necesita saber antes de pulsar es lo
 * otro: que los accesos YA ocurridos no se borran —siguen en la bitácora, con nombre y hora—
 * y que volver a otorgarlo es un trámite, no un favor. Un diálogo del navegador no puede
 * decir eso, no atrapa el foco y cada sistema operativo lo dibuja distinto.
 */
function ModalDeRevocacionDeSoporte({ consentimiento, onCerrar, onConfirmar }: {
  consentimiento: ConsentimientoDeSoporte
  onCerrar: () => void
  onConfirmar: () => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLDivElement>(true, onCerrar)

  return (
    <div className="modal" {...telon(() => onCerrar())}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={idTitulo}>Revocar el consentimiento de soporte</h2>
        <p>
          Desde este momento nadie de la plataforma podrá ver tu contabilidad. Este
          consentimiento seguía vigente hasta el{' '}
          {/* `vigenteHastaUtc` lleva sufijo Utc: es un INSTANTE, y convertirlo a hora local es
              lo correcto. No es una fecha contable, así que no va por `fechaContable` (E1). */}
          <strong>{new Date(consentimiento.vigenteHastaUtc).toLocaleString('es-MX')}</strong>.
        </p>
        <p className="sutil">Otorgado para: {consentimiento.proposito}</p>
        <p className="nota">
          Lo que ya se consultó no se borra: cada acceso quedó en tu bitácora con el nombre de
          quien lo hizo y la hora. Puedes volver a otorgarlo cuando lo necesites, por hasta{' '}
          {DIAS_MAXIMOS} días.
        </p>
        <div className="acciones">
          <button onClick={onCerrar}>Cancelar</button>
          <button className="primario" onClick={onConfirmar}>
            Revocar el consentimiento
          </button>
        </div>
      </div>
    </div>
  )
}

function FormularioDeConsentimiento({ onCerrar, onOtorgado }: { onCerrar: () => void; onOtorgado: () => void }) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLFormElement>(true, onCerrar)
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)

  async function enviar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    setError('')
    setEnviando(true)
    const datos = new FormData(evento.currentTarget)
    try {
      await api.otorgarConsentimientoDeSoporte({
        proposito: String(datos.get('proposito')).trim(),
        diasDeVigencia: Number(datos.get('diasDeVigencia')),
      })
      onOtorgado()
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo otorgar el consentimiento.')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className="modal" {...telon(() => onCerrar())}>
      <form
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        onSubmit={enviar}
      >
        <h2 id={idTitulo}>Otorgar consentimiento de soporte</h2>

        <label>
          Propósito
          <input name="proposito" required maxLength={500} placeholder="P. ej. revisar por qué falla la descarga masiva del cliente X" />
        </label>

        <label>
          Días de vigencia
          <input name="diasDeVigencia" type="number" min={1} max={DIAS_MAXIMOS} defaultValue={7} required />
        </label>

        <p className="sutil">
          Vence solo al cumplirse los días; puedes revocarlo antes. Máximo {DIAS_MAXIMOS} días por consentimiento.
        </p>

        {error && <p className="error" role="alert">{error}</p>}

        <div className="acciones">
          <button type="button" onClick={onCerrar}>Cancelar</button>
          <button type="submit" className="primario" disabled={enviando}>
            {enviando ? 'Otorgando…' : 'Otorgar'}
          </button>
        </div>
      </form>
    </div>
  )
}
